'use client'

import {
    rescheduleConsultation,
    unscheduleConsultation,
} from '@/lib/api/consultations/consultation-api'
import { rescheduleExam, unscheduleExam } from '@/lib/api/exams/exam-api'
import { rescheduleOrder, unscheduleOrder } from '@/lib/api/orders/order-api'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { formatInTimeZone, toZonedTime } from 'date-fns-tz'
import { useEffect, useState } from 'react'
import { MdEvent, MdEventBusy } from 'react-icons/md'
import { toast } from 'react-toastify'

export type ScheduleServiceType = 'PROCEDURE' | 'EXAM' | 'CONSULTATION'

interface ScheduleAppointmentModalProps {
    isOpen: boolean
    onClose: () => void
    serviceType: ScheduleServiceType
    recordId: number
    /** Data atual (ISO do backend). Preenchida = a operação é REMARCAÇÃO. */
    currentScheduledAt?: string | null
    currentArrivalOrder?: boolean
    /** Chamado depois de gravar, para a tela recarregar. */
    onScheduled: () => void
}

/**
 * Marcar (ou remarcar) a data do atendimento sem passar pela tela de edição.
 *
 * A edição carrega o atendimento inteiro — itens, valores, executantes, materiais —
 * e quem só precisa dizer "é quinta às 14h" não tem por que atravessar tudo aquilo,
 * com o risco de esbarrar em outra coisa pelo caminho.
 *
 * **Entra pelo `reschedule`, nos dois casos** — primeira marcação e mudança de data.
 * Ele valida conflito de horário, move (ou cria) o `AgendaEvent`, grava o histórico e
 * avisa o paciente apenas quando havia data antes, que é a definição de remarcação.
 *
 * O `update` da tela de edição não serve aqui: sendo edição parcial, ele revalida o
 * atendimento inteiro contra o que está gravado — um procedimento com taxa de
 * anestesista sem destinatário (dado antigo, comum) recusa qualquer salvamento, e o
 * operador que só queria marcar a data levava um erro sobre repasse.
 *
 * **Remover a data** é a terceira operação, e só aparece quando já existe data. Ela
 * entra pelo `unschedule`, que apaga o `AgendaEvent` (liberando o horário) e devolve o
 * status para "sem data". Pela tela de edição isso era impossível: apagar o campo de
 * data mandava `undefined` ao backend, que lê "não informado" e mantém tudo — a data
 * continuava gravada, o status continuava agendado e o horário continuava ocupado.
 */
export default function ScheduleAppointmentModal({
    isOpen,
    onClose,
    serviceType,
    recordId,
    currentScheduledAt,
    currentArrivalOrder = false,
    onScheduled,
}: ScheduleAppointmentModalProps) {
    const { clientLabel } = useClientLabel()
    const [date, setDate] = useState('')
    const [time, setTime] = useState('')
    const [arrivalOrder, setArrivalOrder] = useState(false)
    const [reason, setReason] = useState('')
    const [saving, setSaving] = useState(false)
    /** Remover a data tira o atendimento da agenda: pede confirmação no próprio modal. */
    const [confirmingRemoval, setConfirmingRemoval] = useState(false)

    const isReschedule = !!currentScheduledAt

    // Reabrir o modal recomeça do que está gravado — inclusive depois de um
    // cancelamento, que não pode deixar rascunho para a próxima abertura.
    useEffect(() => {
        if (!isOpen) return
        if (currentScheduledAt) {
            const local = formatInTimeZone(
                new Date(currentScheduledAt),
                'America/Sao_Paulo',
                "yyyy-MM-dd'T'HH:mm"
            )
            const [d, t] = local.split('T')
            setDate(d || '')
            setTime(t || '')
        } else {
            setDate('')
            setTime('')
        }
        setArrivalOrder(currentArrivalOrder)
        setReason('')
        setConfirmingRemoval(false)
    }, [isOpen, currentScheduledAt, currentArrivalOrder])

    if (!isOpen) return null

    const handleSave = async () => {
        if (!date) {
            toast.error('Informe a data do atendimento.')
            return
        }
        // Ordem de chegada: tem data, não tem hora — o backend zera o horário.
        const localValue = `${date}T${arrivalOrder ? '00:00' : time || '00:00'}`
        const scheduledAt = toZonedTime(
            localValue,
            'America/Sao_Paulo'
        ).toISOString()

        setSaving(true)
        try {
            const payload = {
                scheduledAt,
                arrivalOrder,
                reason: isReschedule ? reason.trim() || undefined : undefined,
            }
            const res =
                serviceType === 'CONSULTATION'
                    ? await rescheduleConsultation(recordId, payload)
                    : serviceType === 'PROCEDURE'
                      ? await rescheduleOrder(recordId, payload)
                      : await rescheduleExam(recordId, payload)
            if (!res.success) {
                toast.error(
                    res.error ||
                        (isReschedule
                            ? 'Não foi possível remarcar o atendimento'
                            : 'Não foi possível agendar o atendimento')
                )
                return
            }
            toast.success(
                isReschedule
                    ? `Atendimento remarcado. O ${clientLabel} foi avisado da nova data.`
                    : 'Atendimento agendado.'
            )
            onScheduled()
            onClose()
        } catch {
            toast.error('Erro ao salvar o agendamento')
        } finally {
            setSaving(false)
        }
    }

    /**
     * Tira a data do atendimento — ele continua existindo, sem dia marcado.
     *
     * O backend apaga o `AgendaEvent` junto: sem isso o horário continuaria ocupado na
     * agenda por um atendimento que não vai acontecer ali.
     */
    const handleUnschedule = async () => {
        setSaving(true)
        try {
            const payload = { reason: reason.trim() || undefined }
            const res =
                serviceType === 'CONSULTATION'
                    ? await unscheduleConsultation(recordId, payload)
                    : serviceType === 'PROCEDURE'
                      ? await unscheduleOrder(recordId, payload)
                      : await unscheduleExam(recordId, payload)
            if (!res.success) {
                toast.error(
                    res.error || 'Não foi possível remover a data do atendimento'
                )
                return
            }
            toast.success(
                'Data removida. O atendimento saiu da agenda e continua sem data.'
            )
            onScheduled()
            onClose()
        } catch {
            toast.error('Erro ao remover a data do atendimento')
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog className="modal modal-open z-50">
            <div className="modal-box max-w-md">
                <div className="flex items-center gap-2">
                    <MdEvent className="text-xl text-secondary" />
                    <h3 className="text-lg font-bold">
                        {isReschedule
                            ? 'Remarcar atendimento'
                            : 'Agendar atendimento'}
                    </h3>
                </div>

                <div className="mt-5 flex flex-col gap-4">
                    <div className="flex flex-wrap gap-3">
                        <div className="form-control min-w-[10rem] flex-1">
                            <label className="label pb-1" htmlFor="schedule-date">
                                <span className="label-text text-sm font-medium">
                                    Data
                                </span>
                            </label>
                            <input
                                id="schedule-date"
                                type="date"
                                className="input input-bordered w-full"
                                value={date}
                                onChange={e => setDate(e.target.value)}
                            />
                        </div>
                        <div className="form-control min-w-[8rem] flex-1">
                            <label className="label pb-1" htmlFor="schedule-time">
                                <span className="label-text text-sm font-medium">
                                    Hora{' '}
                                    <span className="font-normal text-base-content/50">
                                        (opcional)
                                    </span>
                                </span>
                            </label>
                            <input
                                id="schedule-time"
                                type="time"
                                className="input input-bordered w-full disabled:opacity-60"
                                value={time}
                                disabled={arrivalOrder}
                                onChange={e => setTime(e.target.value)}
                            />
                        </div>
                    </div>

                    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-base-300 px-4 py-3">
                        <input
                            type="radio"
                            className="radio radio-sm radio-secondary mt-0.5"
                            checked={arrivalOrder}
                            onChange={() => setArrivalOrder(!arrivalOrder)}
                            onClick={() => setArrivalOrder(!arrivalOrder)}
                        />
                        <span className="text-sm">
                            Atendimento por ordem de chegada (sem hora marcada)
                        </span>
                    </label>

                    {/* Remarcação avisa o paciente; o motivo entra na mensagem e no
                        histórico. Na primeira marcação não há o que justificar. O
                        mesmo campo serve à remoção da data, que também vai ao
                        histórico. */}
                    {isReschedule && (
                        <div className="form-control">
                            <label
                                className="label pb-1"
                                htmlFor="schedule-reason"
                            >
                                <span className="label-text text-sm font-medium">
                                    {confirmingRemoval
                                        ? 'Motivo da remoção'
                                        : 'Motivo da remarcação'}{' '}
                                    <span className="font-normal text-base-content/50">
                                        (opcional)
                                    </span>
                                </span>
                            </label>
                            <input
                                id="schedule-reason"
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex.: pedido do paciente"
                                value={reason}
                                onChange={e => setReason(e.target.value)}
                            />
                        </div>
                    )}

                    {/* Só faz sentido no atendimento que TEM data — o sem data já
                        está desagendado. */}
                    {isReschedule &&
                        (confirmingRemoval ? (
                            <div className="rounded-xl border border-error/40 bg-error/5 px-4 py-3">
                                <p className="text-sm font-medium text-error">
                                    Remover a data deste atendimento?
                                </p>
                                <p className="mt-1 text-xs text-base-content/70">
                                    Ele sai da agenda e o horário é liberado. O
                                    atendimento continua existindo, sem data — a
                                    cobrança e os valores não são alterados.
                                </p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-error btn-sm gap-2"
                                        onClick={handleUnschedule}
                                        disabled={saving}
                                    >
                                        {saving && (
                                            <span className="loading loading-spinner loading-xs" />
                                        )}
                                        Sim, remover a data
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() =>
                                            setConfirmingRemoval(false)
                                        }
                                        disabled={saving}
                                    >
                                        Voltar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-outline btn-error btn-sm gap-2 self-start"
                                onClick={() => setConfirmingRemoval(true)}
                                disabled={saving}
                            >
                                <MdEventBusy className="text-base" />
                                Remover data do agendamento
                            </button>
                        ))}
                </div>

                <div className="modal-action mt-6">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary gap-2"
                        onClick={handleSave}
                        disabled={saving || !date || confirmingRemoval}
                    >
                        {saving && (
                            <span className="loading loading-spinner loading-xs" />
                        )}
                        {isReschedule ? 'Remarcar' : 'Agendar'}
                    </button>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}
